/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { Banknote, Coins } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

/* Import components */
import Api2TimeSeriesPlot from '@/components/api/Api2TimeSeriesPlot';
import KpiValueApi2LastValue from '@/components/api/KpiValueApi2LastValue';
import DashboardGrid from '@/components/layout/DashboardGrid';
import ElectricityPriceRecommendationPanel from '@/components/tabs/pages/ElectricityPriceRecommendationPanel';

/* Import configuration */
import {
    PRICE_TRAFFIC_LIGHT_CRITICAL,
    PRICE_TRAFFIC_LIGHT_URN,
    PRICE_TRAFFIC_LIGHT_WARNING,
} from '@/config/app';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import helper functions */
import { ElectricityPriceAdvisor } from '@/lib/electricity/ElectricityPriceAdvisor';
import { createElectricityPricePoints } from '@/lib/electricity/electricityPricePoints';

/* Import types */
import type { ParsedTimeSeriesCsv } from '@/lib/timeSeries/parseTimeSeriesCsv';

type PriceUnit = 'centPerKwh' | 'euroPerMwh';

const CONSUMPTION_SCENARIO_STORAGE_KEY = 'price-traffic-light.consumption-scenarios';
const PRICE_UNIT_STORAGE_KEY = 'price-traffic-light.unit';

const PRICE_UNITS: Record<PriceUnit, {
    audience: 'Industry' | 'Private';
    conversion: number;
    fractionDigits: number;
    label: string;
}> = {
    euroPerMwh: {
        audience: 'Industry',
        conversion: 1,
        fractionDigits: 2,
        label: '€/MWh',
    },
    centPerKwh: {
        audience: 'Private',
        conversion: 0.1,
        fractionDigits: 2,
        label: 'ct/kWh',
    },
};

interface ConsumptionScenario {
    id: string;
    label: string;
    maxWaitHours: number;
}

const CONSUMPTION_SCENARIOS: Record<PriceUnit, readonly ConsumptionScenario[]> = {
    euroPerMwh: [
        { id: 'production-process', label: 'Production process', maxWaitHours: 2 },
        { id: 'compressed-air-system', label: 'Compressed-air system', maxWaitHours: 4 },
        { id: 'refrigeration-and-cold-storage', label: 'Refrigeration and cold storage', maxWaitHours: 6 },
        { id: 'industrial-process-heat', label: 'Industrial process heat', maxWaitHours: 6 },
        { id: 'commercial-fleet-charging', label: 'Commercial fleet charging', maxWaitHours: 8 },
        { id: 'industrial-battery-storage', label: 'Industrial battery storage', maxWaitHours: 12 },
        { id: 'flexible-industrial-consumption', label: 'Flexible industrial consumption', maxWaitHours: 12 },
        { id: 'flexible-industrial-consumption-next-day', label: 'Flexible industrial consumption next day', maxWaitHours: 24 },
    ],
    centPerKwh: [
        { id: 'dryer', label: 'Dryer', maxWaitHours: 2 },
        { id: 'washing-machine', label: 'Washing machine', maxWaitHours: 3 },
        { id: 'dishwasher', label: 'Dishwasher', maxWaitHours: 3 },
        { id: 'electric-car', label: 'Electric car', maxWaitHours: 5 },
        { id: 'water-heating', label: 'Water heating', maxWaitHours: 6 },
        { id: 'flexible', label: 'Flexible consumption', maxWaitHours: 6 },
        { id: 'battery-storage', label: 'Battery storage', maxWaitHours: 12 },
        { id: 'flexible-consumption-next-day', label: 'Flexible consumption next day', maxWaitHours: 24 },
    ],
};

const DEFAULT_CONSUMPTION_SCENARIO_IDS: Record<PriceUnit, string> = {
    euroPerMwh: 'production-process',
    centPerKwh: 'washing-machine',
};

function getInitialConsumptionScenarioIds(): Record<PriceUnit, string> {
    const defaults = { ...DEFAULT_CONSUMPTION_SCENARIO_IDS };

    if (typeof window === 'undefined') {
        return defaults;
    }

    const storedScenarioIds = window.localStorage.getItem(CONSUMPTION_SCENARIO_STORAGE_KEY);

    if (!storedScenarioIds) {
        return defaults;
    }

    try {
        const parsedScenarioIds: unknown = JSON.parse(storedScenarioIds);

        if (typeof parsedScenarioIds !== 'object' || parsedScenarioIds === null) {
            return defaults;
        }

        return (Object.keys(defaults) as PriceUnit[]).reduce(
            (scenarioIds, unit) => {
                const storedScenarioId = (parsedScenarioIds as Record<string, unknown>)[unit];

                scenarioIds[unit] = typeof storedScenarioId === 'string' &&
                    CONSUMPTION_SCENARIOS[unit].some(({ id }) => id === storedScenarioId)
                    ? storedScenarioId
                    : defaults[unit];

                return scenarioIds;
            },
            defaults,
        );
    } catch {
        return defaults;
    }
}

function getInitialPriceUnit(): PriceUnit {
    if (typeof window === 'undefined') {
        return 'centPerKwh';
    }

    const storedPriceUnit = window.localStorage.getItem(PRICE_UNIT_STORAGE_KEY);

    return storedPriceUnit === 'centPerKwh' || storedPriceUnit === 'euroPerMwh'
        ? storedPriceUnit
        : 'centPerKwh';
}

function formatPriceMarkerTimestamp(timestamp: Date): string {
    const parts = new Intl.DateTimeFormat('en-CA', {
        day: '2-digit',
        hour: '2-digit',
        hourCycle: 'h23',
        minute: '2-digit',
        month: '2-digit',
        second: '2-digit',
        timeZone: 'Europe/Berlin',
        year: 'numeric',
    }).formatToParts(timestamp);
    const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));

    return `${values.year}-${values.month}-${values.day} ${values.hour}:${values.minute}:${values.second}`;
}

function PriceTrafficLightTab() {
    const { locale, t } = useI18n();
    const [selectedUnit, setSelectedUnit] = useState<PriceUnit>(getInitialPriceUnit);
    const [selectedScenarioIds, setSelectedScenarioIds] = useState<Record<PriceUnit, string>>(
        getInitialConsumptionScenarioIds,
    );
    const [parsedPriceTimeSeries, setParsedPriceTimeSeries] = useState<ParsedTimeSeriesCsv | null>(null);
    const [priceDataUpdatedAt, setPriceDataUpdatedAt] = useState<Date | null>(null);
    const [referenceTime, setReferenceTime] = useState(() => new Date());
    const priceUnit = PRICE_UNITS[selectedUnit];
    const consumptionScenarios = CONSUMPTION_SCENARIOS[selectedUnit];
    const selectedScenarioId = selectedScenarioIds[selectedUnit];
    const selectedScenario = consumptionScenarios.find(
        ({ id }) => id === selectedScenarioId,
    ) ?? consumptionScenarios[0];
    const warningBandThreshold = PRICE_TRAFFIC_LIGHT_WARNING * priceUnit.conversion;
    const criticalBandThreshold = PRICE_TRAFFIC_LIGHT_CRITICAL * priceUnit.conversion;
    const evaluationRangeStart = formatPriceMarkerTimestamp(referenceTime);
    const evaluationRangeEnd = formatPriceMarkerTimestamp(new Date(
        referenceTime.getTime() + selectedScenario.maxWaitHours * 60 * 60 * 1000,
    ));
    const prices = useMemo(
        () => createElectricityPricePoints(parsedPriceTimeSeries, PRICE_TRAFFIC_LIGHT_URN),
        [parsedPriceTimeSeries],
    );
    const priceAssessment = useMemo(() => {
        if (prices.length === 0) {
            return null;
        }

        const advisor = new ElectricityPriceAdvisor({
            maxWaitHours: selectedScenario.maxWaitHours,
            prices,
            thresholds: {
                cheapBelow: PRICE_TRAFFIC_LIGHT_WARNING,
                expensiveAbove: PRICE_TRAFFIC_LIGHT_CRITICAL,
            },
        });

        try {
            return {
                recommendation: advisor.getRecommendation(referenceTime),
                timeline: advisor.getInterestingTimeline(referenceTime),
            };
        } catch {
            return null;
        }
    }, [prices, referenceTime, selectedScenario.maxWaitHours]);

    useEffect(() => {
        window.localStorage.setItem(
            CONSUMPTION_SCENARIO_STORAGE_KEY,
            JSON.stringify(selectedScenarioIds),
        );
    }, [selectedScenarioIds]);

    useEffect(() => {
        window.localStorage.setItem(PRICE_UNIT_STORAGE_KEY, selectedUnit);
    }, [selectedUnit]);

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            setReferenceTime(new Date());
        }, 60_000);

        return () => window.clearInterval(intervalId);
    }, []);

    const handleParsedPriceTimeSeriesChange = (nextTimeSeries: ParsedTimeSeriesCsv | null) => {
        setParsedPriceTimeSeries(nextTimeSeries);
        setPriceDataUpdatedAt(nextTimeSeries ? new Date() : null);
    };

    return (
        <DashboardGrid desktopCols={['3fr', '1fr']} desktopRows={['160px', '1fr']} breakpoint="2xl" aria-label={t('Price Traffic Light')}>
            <DashboardGrid.Item className="grid min-h-0 gap-4 2xl:grid-rows-[auto_minmax(0,1fr)]" desktopRowSpan={2}>
                <section className="dashboard-panel flex items-stretch justify-start p-5">
                    <div className="grid w-full grid-cols-1 gap-y-4 text-left md:grid-cols-2 md:divide-x md:divide-base-content">
                        <div className="flex min-h-12 items-center gap-3 md:pr-5">
                            <span className="text-sm font-semibold">{t('Unit')}</span>
                            <label className="toggle border border-base-300 bg-base-200 text-base-content">
                                <input
                                    type="checkbox"
                                    checked={selectedUnit === 'euroPerMwh'}
                                    onChange={(event) => {
                                        setSelectedUnit(event.target.checked ? 'euroPerMwh' : 'centPerKwh');
                                    }}
                                    aria-label={t('Unit')}
                                />
                                <Coins className="h-4 w-4" aria-hidden="true" />
                                <Banknote className="h-4 w-4" aria-hidden="true" />
                            </label>
                            <span className="w-36 text-sm">
                                {priceUnit.label} – {t(priceUnit.audience)}
                            </span>
                        </div>

                        <div className="flex min-h-12 items-center md:pl-5">
                            <div className="flex w-full items-center gap-3">
                                <label className="text-sm font-semibold" htmlFor="price-consumption-scenario">
                                    {t('What would you like to use?')}
                                </label>
                                <select
                                    id="price-consumption-scenario"
                                    className="select select-bordered min-w-0 flex-1 font-normal"
                                    value={selectedScenarioId}
                                    onChange={(event) => {
                                        setSelectedScenarioIds((currentScenarioIds) => ({
                                            ...currentScenarioIds,
                                            [selectedUnit]: event.target.value,
                                        }));
                                    }}
                                >
                                    {consumptionScenarios.map((scenario) => (
                                        <option key={scenario.id} value={scenario.id}>
                                            {t(scenario.label)} ({t('Can start up to {hours} hours later', {
                                                hours: scenario.maxWaitHours,
                                            })})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="dashboard-panel min-h-0 items-stretch justify-start">
                    <ElectricityPriceRecommendationPanel
                        conversion={priceUnit.conversion}
                        maxWaitHours={selectedScenario.maxWaitHours}
                        recommendation={priceAssessment?.recommendation ?? null}
                        referenceTime={referenceTime}
                        timeline={priceAssessment?.timeline ?? []}
                        unit={priceUnit.label}
                        updatedAt={priceDataUpdatedAt}
                    />
                </section>
            </DashboardGrid.Item>

            <DashboardGrid.Item as="section" className="dashboard-panel">
                <KpiValueApi2LastValue
                    date="NOW"
                    urn={PRICE_TRAFFIC_LIGHT_URN}
                    title={t('Electricity price')}
                    conversion={priceUnit.conversion}
                    fractionDigits={priceUnit.fractionDigits}
                    unit={priceUnit.label}
                    trafficLight={{
                        labels: {
                            danger: t('Expensive'),
                            success: t('Favourable'),
                            warning: t('Normal'),
                        },
                        rules: [
                            { when: 'gt', value: PRICE_TRAFFIC_LIGHT_CRITICAL, severity: 'danger' },
                            { when: 'gte', value: PRICE_TRAFFIC_LIGHT_WARNING, severity: 'warning' },
                        ],
                        fallback: 'success',
                    }}
                    showLegend={true}
                    info={t('The day-ahead price shows the wholesale electricity price for the Germany/Luxembourg market area.')}
                />
            </DashboardGrid.Item>

            <DashboardGrid.Item as="section" className="dashboard-panel" mobileMinHeight="500px">
                <Api2TimeSeriesPlot
                    from="now-12hours"
                    to="now+36hours"
                    urn={PRICE_TRAFFIC_LIGHT_URN}
                    conversion={priceUnit.conversion}
                    legendNames={{
                        [PRICE_TRAFFIC_LIGHT_URN]: t('Electricity price'),
                    }}
                    onParsedTimeSeriesChange={handleParsedPriceTimeSeriesChange}
                    revision={referenceTime.getTime()}
                    timeBands={[{
                        borderColor: 'rgba(14, 165, 233, 0.55)',
                        color: 'rgba(14, 165, 233, 0.12)',
                        from: evaluationRangeStart,
                        to: evaluationRangeEnd,
                    }]}
                    title={`${t('Electricity price')} [${priceUnit.label}]`}
                    yAxisTitle={priceUnit.label}
                    valueBands={[
                        { to: warningBandThreshold, color: 'rgba(34, 197, 94, 0.11)' },
                        { from: warningBandThreshold, to: criticalBandThreshold, color: 'rgba(245, 158, 11, 0.11)' },
                        { from: criticalBandThreshold, color: 'rgba(239, 68, 68, 0.11)' },
                    ]}
                    verticalMarkers={[{
                        legendUnit: priceUnit.label,
                        label: t('Now'),
                        locale,
                        showValueInAnnotation: true,
                        showValueGuide: true,
                        timestamp: evaluationRangeStart,
                        valueSelection: 'previous',
                    }, {
                        color: '#0ea5e9',
                        label: '',
                        timestamp: evaluationRangeEnd,
                    }]}
                />
            </DashboardGrid.Item>

        </DashboardGrid>
    );
}

export default PriceTrafficLightTab;
